import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ManagementColumns from '../../../components/management-columns/ManagementColumns';
import { FilterOption } from '../../../components/custom-input/FilterDropdown';
import OpenSVG from '../../../components/svgs/Open.svg';
import { Modal } from '../../../components';
import AccountTankMaintenancePanel from '../../../components/tank-maintenance/AccountTankMaintenancePanel';
import AccountTankMaintenanceSettingsModal from '../../../components/tank-maintenance/AccountTankMaintenanceSettingsModal';
import TankDueBadge from '../../../components/tank-maintenance/TankDueBadge';
import OnFarmSiteModal from './OnFarmSiteModal';
import OnFarmTankModal from './OnFarmTankModal';
import OnFarmCanisterModal from './OnFarmCanisterModal';
import { ManagementColumnFilterTypes } from '../../../types/enums';
import { AccountRoles } from '../../../types/enums/accountRoles.enum';
import { AppRoles } from '../../../types/enums/appRoles.enum';
import {
  getAccountStorageCanisterCount,
  getAccountStorageCanisters,
  getAccountStorageSites,
  getAccountStorageTankInventoryCount,
  getAccountStorageTanks,
} from '../../../api/accountStorageItemsApi';
import { getAccountTankMaintenanceDueList } from '../../../api/accountTankMaintenanceApi';
import {
  AccountStorageCanister,
  AccountStorageSite,
  AccountStorageTank,
  AccountTankDueSummary,
  CanisterContentsAutoFill,
  PagedResponse,
} from '../../../types/interfaces';
import { showToast } from '../../../services/toast.service';
import { PermissionService } from '../../../services/permission.service';
import { useAppSelector } from '../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../hooks/useOnFarmActionBlocked';
import { LABEL_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../../constants/common';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { toastMessages } from '../../../constants/errorMessages';
import { buildDueSummaryMap, getDaysUntilDue, getDueStatusFromDays } from '../../../utils/tankMaintenance.utils';
import './onFarmLocationManagement.scss';

interface AccountStorageSiteOption extends AccountStorageSite {
  storageTankCount?: number;
}

interface AccountStorageTankOption extends AccountStorageTank {
  storageCanisterCount?: number;
}

interface AccountStorageCanisterOption extends AccountStorageCanister {
  storageCount?: number;
}

const OnFarmLocationManagement: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(state => state.user.enabled);
  const verified = useAppSelector(state => state.user.verified);
  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);
  const isOnFarmLocked = useOnFarmActionBlocked();
  const canEditMaintenance = permissionService.userHasInventoryPermission() && !isOnFarmLocked;

  const [totalSites, setTotalSites] = useState<number>();
  const [totalTanks, setTotalTanks] = useState<number>();
  const [totalCanisters, setTotalCanisters] = useState<number>();

  const [showFilter, setShowFilter] = useState<FilterOption<ManagementColumnFilterTypes>>({
    name: LABEL_CONSTANTS.ACTIVE_INACTIVE,
    value: ManagementColumnFilterTypes.BOTH,
  });
  const [sites, setSites] = useState<AccountStorageSiteOption[]>([]);
  const [tanks, setTanks] = useState<AccountStorageTankOption[]>([]);
  const [canisters, setCanisters] = useState<AccountStorageCanisterOption[]>([]);
  const [selectedSite, setSelectedSite] = useState<AccountStorageSiteOption>();
  const [selectedTank, setSelectedTank] = useState<AccountStorageTankOption>();
  const [selectedCanister, setSelectedCanister] = useState<AccountStorageCanisterOption>();

  const [newSiteId, setNewSiteId] = useState<number>();
  const [newTankId, setNewTankId] = useState<number>();
  const [newCanisterId, setNewCanisterId] = useState<number>();

  const [siteRefresh, setSiteRefresh] = useState<number>(0);
  const [tankRefresh, setTankRefresh] = useState<number>(0);
  const [canisterRefresh, setCanisterRefresh] = useState<number>(0);
  const [maintenanceRefresh, setMaintenanceRefresh] = useState<number>(0);
  const [dueByTankId, setDueByTankId] = useState<Map<number, AccountTankDueSummary>>(new Map());
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);

  const [tankInventoryCount, setTankInventoryCount] = useState<number>();
  const [canisterCount, setCanisterCount] = useState<number>();

  const [offsetSites, setOffsetSites] = useState<number>(0);
  const [offsetTanks, setOffsetTanks] = useState<number>(0);
  const [offsetCanisters, setOffsetCanisters] = useState<number>(0);

  const hasAllSites: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const hasAllTanks: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const hasAllCanisters: React.MutableRefObject<boolean> = useRef<boolean>(false);

  const isLoadingAdditionalSites: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingAdditionalTanks: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingAdditionalCanisters: React.MutableRefObject<boolean> = useRef<boolean>(false);

  const callNumberSites: React.MutableRefObject<number> = useRef<number>(0);
  const callNumberTanks: React.MutableRefObject<number> = useRef<number>(0);
  const callNumberCanisters: React.MutableRefObject<number> = useRef<number>(0);

  const [loadingSites, setLoadingSites] = useState<boolean>(false);
  const [loadingTanks, setLoadingTanks] = useState<boolean>(false);
  const [loadingCanisters, setLoadingCanisters] = useState<boolean>(false);

  const limit: number = 10;
  const dueList = Array.from(dueByTankId.values());
  const overdueCount = dueList.filter(item => (getDaysUntilDue(item.nextDueDate) ?? item.daysUntilDue) < 0).length;

  useEffect(() => {
    getTotals();
  }, [newSiteId, newTankId, newCanisterId]);

  useEffect(() => {
    const loadDueList = async () => {
      try {
        const { data } = await getAccountTankMaintenanceDueList();
        setDueByTankId(buildDueSummaryMap(data ?? []));
      } catch {
        setDueByTankId(new Map());
      }
    };
    loadDueList();
  }, [maintenanceRefresh, tankRefresh]);

  const getTotals = async () => {
    try {
      const [{ data: sitesResponse }, { data: tanksResponse }, { data: canistersResponse }] = await Promise.all([
        getAccountStorageSites({ limit: 1 }),
        getAccountStorageTanks({ limit: 1 }),
        getAccountStorageCanisters({ limit: 1 }),
      ]);
      setTotalSites((sitesResponse as PagedResponse<AccountStorageSite>).total);
      setTotalTanks((tanksResponse as PagedResponse<AccountStorageTank>).total);
      setTotalCanisters((canistersResponse as PagedResponse<AccountStorageCanister>).total);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    getSites();
  }, [offsetSites]);

  useEffect(() => {
    getTanks();
  }, [offsetTanks]);

  useEffect(() => {
    getCanisters();
  }, [offsetCanisters]);

  const getSites = async () => {
    if (hasAllSites.current) {
      return;
    }
    setLoadingSites(true);
    callNumberSites.current++;
    const callNum = callNumberSites.current;
    try {
      const query = { filter: showFilter.value, limit: limit, offset: offsetSites, sort: 'Name' };
      const { data: accountStorageSites } = await getAccountStorageSites(query);
      if (callNum === callNumberSites.current) {
        const sitesList = (accountStorageSites as PagedResponse<AccountStorageSite>).result;
        const newSites = await Promise.all(
          sitesList.map(async (s: AccountStorageSite) => {
            try {
              const { data: tResponse } = await getAccountStorageTanks({
                filter: 'accountStorageSiteId eq ' + s.accountStorageSiteId,
                limit: 1,
              });
              return { ...s, storageTankCount: (tResponse as PagedResponse<AccountStorageTank>).total };
            } catch {
              return s;
            }
          }),
        );
        if (callNum === callNumberSites.current) {
          setSites(offsetSites > 0 ? [...sites, ...newSites] : newSites);
          hasAllSites.current = newSites.length === 0;
        }
      }
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setLoadingSites(false);
    }
  };

  const getTanks = async () => {
    if (hasAllTanks.current) {
      return;
    }
    setLoadingTanks(true);
    callNumberTanks.current++;
    const callNum = callNumberTanks.current;
    try {
      if (selectedSite) {
        const query = {
          filter: showFilter.value + 'accountStorageSiteId eq ' + selectedSite!.accountStorageSiteId.toString(),
          limit: limit,
          offset: offsetTanks,
          sort: 'Name',
        };
        const { data: accountStorageTanks } = await getAccountStorageTanks(query);
        if (callNum === callNumberTanks.current) {
          const tanksList = (accountStorageTanks as PagedResponse<AccountStorageTank>).result;
          const newTanks: AccountStorageTankOption[] = await Promise.all(
            tanksList.map(async (t: AccountStorageTank) => {
              try {
                const { data: cResponse } = await getAccountStorageCanisters({
                  filter: 'accountStorageTankId eq ' + t.accountStorageTankId,
                  limit: 1,
                });
                return { ...t, storageCanisterCount: (cResponse as PagedResponse<AccountStorageCanister>).total };
              } catch {
                return t;
              }
            }),
          );

          if (callNum === callNumberTanks.current) {
            setTanks(offsetTanks > 0 ? [...tanks, ...newTanks] : newTanks);
            hasAllTanks.current = newTanks.length === 0;
          }
        }
      } else {
        setTanks([]);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setSelectedTank(undefined);
      setSelectedCanister(undefined);
      setCanisters([]);
    } finally {
      setLoadingTanks(false);
    }
  };

  const getCanisters = async () => {
    if (hasAllCanisters.current) {
      return;
    }
    setLoadingCanisters(true);
    callNumberCanisters.current++;
    const callNum = callNumberCanisters.current;
    try {
      if (selectedTank) {
        const query = {
          filter: showFilter.value + 'accountStorageTankId eq ' + selectedTank!.accountStorageTankId.toString(),
          limit: limit,
          offset: offsetCanisters,
          sort: 'Name',
        };
        const { data: accountStorageCanisters } = await getAccountStorageCanisters(query);
        if (callNum === callNumberCanisters.current) {
          const canisterList = (accountStorageCanisters as PagedResponse<AccountStorageCanister>).result;
          const newCanisters: AccountStorageCanisterOption[] = await Promise.all(
            canisterList.map(async (c: AccountStorageCanister) => {
              try {
                const { data: cResponse } = await getAccountStorageCanisterCount(c.accountStorageCanisterId);
                return { ...c, storageCount: cResponse };
              } catch {
                return c;
              }
            }),
          );

          setCanisters(offsetCanisters > 0 ? [...canisters, ...newCanisters] : newCanisters);
          hasAllCanisters.current = newCanisters.length === 0;
        }
      } else {
        setCanisters([]);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setSelectedCanister(undefined);
    } finally {
      setLoadingCanisters(false);
    }
  };

  useEffect(() => {
    hasAllSites.current = false;
    setSites([]);
    setSelectedSite(undefined);
    if (offsetSites === 0) {
      getSites();
    } else {
      setOffsetSites(0);
    }
  }, [showFilter, siteRefresh]);

  useEffect(() => {
    hasAllTanks.current = false;
    setTanks([]);
    setSelectedTank(undefined);
    if (offsetTanks === 0) {
      getTanks();
    } else {
      setOffsetTanks(0);
    }
  }, [selectedSite, showFilter, tankRefresh]);

  useEffect(() => {
    hasAllCanisters.current = false;
    setCanisters([]);
    setSelectedCanister(undefined);
    if (offsetCanisters === 0) {
      getCanisters();
    } else {
      setOffsetCanisters(0);
    }
  }, [selectedTank, showFilter, canisterRefresh]);

  useEffect(() => {
    const getCanisterCount = async (accountStorageCanisterId: number) => {
      try {
        const { data: count } = await getAccountStorageCanisterCount(accountStorageCanisterId);
        setCanisterCount(count);
      } catch {
        setCanisterCount(undefined);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };
    if (selectedCanister?.accountStorageCanisterId) {
      getCanisterCount(selectedCanister?.accountStorageCanisterId);
    } else {
      setCanisterCount(undefined);
    }
  }, [selectedCanister]);

  useEffect(() => {
    const getTankInventoryCount = async (accountStorageTankId: number) => {
      try {
        const { data: count } = await getAccountStorageTankInventoryCount(accountStorageTankId);
        setTankInventoryCount(count);
      } catch {
        setTankInventoryCount(undefined);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };

    if (selectedTank?.accountStorageTankId) {
      getTankInventoryCount(selectedTank?.accountStorageTankId);
    } else {
      setTankInventoryCount(undefined);
    }
  }, [selectedTank]);

  const loadMoreSites = () => {
    setOffsetSites(offsetSites + limit);
  };

  const loadMoreTanks = () => {
    setOffsetTanks(offsetTanks + limit);
  };

  const loadMoreCanisters = () => {
    setOffsetCanisters(offsetCanisters + limit);
  };

  useEffect(() => {
    isLoadingAdditionalSites.current = false;
  }, [sites]);

  useEffect(() => {
    const getSingleSite = async (id: number) => {
      const { data: accountStorageSites } = await getAccountStorageSites({ filter: `AccountStorageSiteId eq ${id}` });
      if ((accountStorageSites as AccountStorageSite[]).length > 0) {
        const s = (accountStorageSites as AccountStorageSite[])[0];
        setSelectedSite(s);
      }
    };

    if (newSiteId) {
      getSingleSite(newSiteId);
    }
  }, [newSiteId]);

  useEffect(() => {
    isLoadingAdditionalTanks.current = false;
  }, [tanks]);

  useEffect(() => {
    const getSingleTank = async (id: number) => {
      const { data: accountStorageTanks } = await getAccountStorageTanks({ filter: `AccountStorageTankId eq ${id}` });
      if ((accountStorageTanks as AccountStorageTank[]).length > 0) {
        const t = (accountStorageTanks as AccountStorageTank[])[0];
        setSelectedTank(t);

        setSites(
          sites.map((site: AccountStorageSiteOption) => {
            if (site.accountStorageSiteId === t.accountStorageSiteId) {
              return { ...site, storageTankCount: (site.storageTankCount ?? 0) + 1 };
            }
            return site;
          }),
        );
      }
    };

    if (newTankId) {
      getSingleTank(newTankId);
    }
  }, [newTankId]);

  useEffect(() => {
    isLoadingAdditionalCanisters.current = false;
  }, [canisters]);

  useEffect(() => {
    const getSingleCanister = async (id: number) => {
      const { data: accountStorageCanisters } = await getAccountStorageCanisters({ filter: `AccountStorageCanisterId eq ${id}` });
      if ((accountStorageCanisters as AccountStorageCanister[]).length > 0) {
        const c = (accountStorageCanisters as AccountStorageCanister[])[0];
        setSelectedCanister(c);

        setTanks(
          tanks.map((tank: AccountStorageTankOption) => {
            if (tank.accountStorageTankId === c.accountStorageTankId) {
              return { ...tank, storageCanisterCount: (tank.storageCanisterCount ?? 0) + 1 };
            }
            return tank;
          }),
        );
      }
    };

    if (newCanisterId) {
      getSingleCanister(newCanisterId);
    }
  }, [newCanisterId]);

  const openCanisterContents = (canister: AccountStorageCanister) => {
    if (selectedSite && selectedTank) {
      const autoFill: CanisterContentsAutoFill = {
        site: { value: selectedSite?.accountStorageSiteId.toString(), name: selectedSite?.name },
        tank: { value: selectedTank?.accountStorageTankId.toString(), name: selectedTank?.name },
        canister: { value: canister.accountStorageCanisterId.toString(), name: canister.name },
      };
      navigate(ROUTE_PATHS.APP_ON_FARM_MANAGEMENT_CANISTER_CONTENTS, { state: autoFill });
    }
  };

  return (
    <div className="on-farm-location-management">
      {showSettingsModal && (
        <Modal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)}>
          <AccountTankMaintenanceSettingsModal
            close={() => setShowSettingsModal(false)}
            canEdit={canEditMaintenance}
            onSaved={() => setMaintenanceRefresh(prev => prev + 1)}
          />
        </Modal>
      )}

      <div className="tank-maintenance-top">
        {dueList.length > 0 ? (
          <div className={`tank-maintenance-due-banner${overdueCount > 0 ? ' tank-maintenance-due-banner--overdue' : ''}`}>
            <p className="due-banner-text">
              <strong className="due-count">{dueList.length}</strong>
              {` ${LABEL_CONSTANTS.TANK_MAINTENANCE_DUE_BANNER}`}
              {overdueCount > 0 && ` (${overdueCount} ${LABEL_CONSTANTS.OVERDUE.toLowerCase()})`}
            </p>
          </div>
        ) : (
          <div className="tank-maintenance-due-banner tank-maintenance-due-banner--empty">
            <p className="due-banner-text">{LABEL_CONSTANTS.TANKS_UP_TO_DATE}</p>
          </div>
        )}
        <button
          type="button"
          className="green inverted button tank-maintenance-settings-btn"
          onClick={() => setShowSettingsModal(true)}>
          {LABEL_CONSTANTS.MAINTENANCE_SETTINGS}
        </button>
      </div>

      <ManagementColumns
        header={PAGE_HEADER_CONSTANTS.ON_FARM_LOCATION_INFO}
        filter={showFilter}
        setFilter={setShowFilter}
        headerStats={[
          { value: totalSites, label: LABEL_CONSTANTS.SITES },
          { value: totalTanks, label: LABEL_CONSTANTS.TANKS },
          { value: totalCanisters, label: LABEL_CONSTANTS.CANISTERS },
        ]}
        infoStats={[
          { label: LABEL_CONSTANTS.TANK_INVENTORY_COUNT, value: tankInventoryCount },
          { label: LABEL_CONSTANTS.CANISTER_INVENTORY_COUNT, value: canisterCount },
        ]}
        columns={[
          {
            header: LABEL_CONSTANTS.SITES,
            items: sites,
            selectedItemId: selectedSite?.accountStorageSiteId,
            getItemId: (item: AccountStorageSite) => item.accountStorageSiteId,
            getItemAddModal: close => (
              <OnFarmSiteModal
                mode="add"
                setSelectedId={setNewSiteId}
                close={() => {
                  setSiteRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            addItemButton: { label: LABEL_CONSTANTS.SITE, disabled: isOnFarmLocked },
            editItemDisabled: isOnFarmLocked,
            getItemLabel: site => (
              <>
                <span className="location-item-main">
                  <span className={site.active ? '' : 'inactive'}>
                    {site.name}
                    {!site.active && <> ({LABEL_CONSTANTS.INACTIVE})</>}
                  </span>
                  {site.siteCode && (
                    <span className="location-item-code" title={LABEL_CONSTANTS.SITE_CODE}>
                      {site.siteCode}
                    </span>
                  )}
                </span>
                {site.storageTankCount !== undefined && <span className="item-count-chip">{site.storageTankCount}</span>}
              </>
            ),
            itemSelect: site => {
              setSelectedSite(site);
              setSelectedTank(undefined);
              setSelectedCanister(undefined);
            },
            getItemEditModal: (close: () => void, site: AccountStorageSiteOption) => (
              <OnFarmSiteModal
                mode="edit"
                site={site}
                close={() => {
                  setSiteRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            scroll: () => {
              if (!isLoadingAdditionalSites.current) {
                isLoadingAdditionalSites.current = true;
                loadMoreSites();
              }
            },
            loading: loadingSites,
          },
          {
            header: LABEL_CONSTANTS.TANKS,
            items: tanks,
            selectedItemId: selectedTank?.accountStorageTankId,
            getItemId: (item: AccountStorageTank) => item.accountStorageTankId,
            getItemAddModal: close => (
              <OnFarmTankModal
                mode="add"
                site={selectedSite!}
                setSelectedId={setNewTankId}
                close={() => {
                  setTankRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            addItemButton: { label: LABEL_CONSTANTS.TANK, disabled: selectedSite === undefined || isOnFarmLocked },
            editItemDisabled: isOnFarmLocked,
            getItemLabel: tank => {
              const due = dueByTankId.get(tank.accountStorageTankId);
              const daysUntilDue = due ? getDaysUntilDue(due.nextDueDate) ?? due.daysUntilDue : undefined;
              return (
                <span className="tank-label-row">
                  <span className="location-item-main">
                    <span className={tank.active ? '' : 'inactive'}>
                      {tank.name}
                      {!tank.active && <> ({LABEL_CONSTANTS.INACTIVE})</>}
                    </span>
                    {tank.tankCode && (
                      <span className="location-item-code" title={LABEL_CONSTANTS.TANK_CODE}>
                        {tank.tankCode}
                      </span>
                    )}
                  </span>
                  {tank.storageCanisterCount !== undefined && (
                    <span className="item-count-chip">{tank.storageCanisterCount}</span>
                  )}
                  {due && daysUntilDue !== undefined && (
                    <TankDueBadge status={getDueStatusFromDays(daysUntilDue)} daysUntilDue={daysUntilDue} />
                  )}
                </span>
              );
            },
            itemSelect: tank => {
              setSelectedTank(tank);
              setSelectedCanister(undefined);
            },
            getItemEditModal: (close: () => void, tank: AccountStorageTankOption) => (
              <OnFarmTankModal
                mode="edit"
                site={selectedSite!}
                tank={tank}
                close={() => {
                  setTankRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            scroll: () => {
              if (!isLoadingAdditionalTanks.current) {
                isLoadingAdditionalTanks.current = true;
                loadMoreTanks();
              }
            },
            loading: loadingTanks && selectedSite !== undefined,
          },
          {
            header: LABEL_CONSTANTS.CANISTERS,
            items: canisters,
            selectedItemId: selectedCanister?.accountStorageCanisterId,
            getItemId: (item: AccountStorageCanister) => item.accountStorageCanisterId,
            getItemAddModal: close => (
              <OnFarmCanisterModal
                mode="add"
                site={selectedSite!}
                tank={selectedTank!}
                setSelectedId={setNewCanisterId}
                close={() => {
                  setCanisterRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            addItemButton: {
              label: LABEL_CONSTANTS.CANISTER,
              disabled: selectedSite === undefined || selectedTank === undefined || isOnFarmLocked,
            },
            editItemDisabled: isOnFarmLocked,
            getItemLabel: canister => (
              <>
                <span className="location-item-main">
                  <span className={canister.active ? '' : 'inactive'}>
                    {canister.name}
                    {!canister.active && <> ({LABEL_CONSTANTS.INACTIVE})</>}
                  </span>
                  {canister.canisterCode && (
                    <span className="location-item-code" title={LABEL_CONSTANTS.CANISTER_CODE}>
                      {canister.canisterCode}
                    </span>
                  )}
                </span>
                <span className="canister-contents-label">
                  {canister.storageCount !== undefined && canister.storageCount > 0 && (
                    <span className="item-count-chip">{canister.storageCount}</span>
                  )}
                  <button className="icon button" onClick={() => openCanisterContents(canister)}>
                    <OpenSVG />
                  </button>
                </span>
              </>
            ),
            itemSelect: canister => setSelectedCanister(canister),
            getItemEditModal: (close: () => void, canister: AccountStorageCanisterOption) => (
              <OnFarmCanisterModal
                mode="edit"
                site={selectedSite!}
                tank={selectedTank!}
                canister={canister}
                close={() => {
                  setCanisterRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            scroll: () => {
              if (!isLoadingAdditionalCanisters.current) {
                isLoadingAdditionalCanisters.current = true;
                loadMoreCanisters();
              }
            },
            loading: loadingCanisters && selectedTank !== undefined,
          },
        ]}
      />

      {selectedTank && (
        <AccountTankMaintenancePanel
          tankId={selectedTank.accountStorageTankId}
          tankName={selectedTank.name}
          siteName={selectedSite?.name}
          canEdit={canEditMaintenance}
          refreshKey={maintenanceRefresh}
          onChanged={() => setMaintenanceRefresh(prev => prev + 1)}
        />
      )}
    </div>
  );
};

export default OnFarmLocationManagement;
